import { FullScreenProgress } from '@/common/components'
import { config as appConfig } from '@/core'
import { ErrorPageType } from '@/core/errors'
import { History, HistoryContext, Location, LocationContext } from '@/core/history'
import { RouteResponse, resolveRoute } from '@/core/router'
import type { TenantData } from '@/core/types'
import { getAmplitudeApiKey } from '@/core/utils'
import { TenantMiddleware, UserMiddleware } from '@/features/auth'
import { persistor } from '@/store'
import theme from '@/theme'
import * as amplitude from '@amplitude/analytics-browser'
import { CssBaseline } from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { ErrorInfo, useEffect, useLayoutEffect, useState } from 'react'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { PersistGate } from 'redux-persist/integration/react'
import { ErrorPage } from './ErrorPage'

type AppProps = {
  history: History
  tenant: TenantData
}

type Page = {
  route?: RouteResponse
  location: Location
}

const ErrorFallback = ({ error }: FallbackProps): JSX.Element => <ErrorPage error={error as ErrorPageType} />

const isFirstChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'ChunkLoadError' && sessionStorage.getItem('retry-lazy-refreshed') !== 'true'

const handleRenderError = (error: unknown, info: ErrorInfo): void => {
  if (isFirstChunkLoadError(error)) {
    sessionStorage.setItem('retry-lazy-refreshed', 'true')
    window.location.reload()
    return
  }

  console.error(error, info)
}

export const App = ({ history, tenant }: AppProps): JSX.Element => {
  const [{ route, location }, setPage] = useState<Page>({ location: history.location })
  const routeError = route?.error as ErrorPageType | undefined
  const title = route?.title

  useEffect(() => {
    let shownPathname: string | undefined

    const show = (next: Location): void => {
      if (next.pathname === shownPathname) {
        setPage((page) => ({ ...page, location: next }))
        return
      }

      resolveRoute({ path: next.pathname }).then((route) => {
        if (route.error) console.error(route.error)
        if (history.location.pathname !== next.pathname) return
        shownPathname = next.pathname
        setPage({ route, location: history.location })
      })
    }

    show(history.location)
    return history.listen((update) => show(update.location))
  }, [history])

  useEffect(() => {
    if (!amplitude.getDeviceId()) {
      amplitude.init(getAmplitudeApiKey() || '', {
        defaultTracking: true,
        serverZone: 'EU',
      })
    }
  }, [])

  useLayoutEffect(() => {
    if (title) document.title = `${title} - ${appConfig.app.name}`
  }, [title])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleRenderError} resetKeys={[location]}>
          {routeError ? (
            <ErrorPage error={routeError} />
          ) : (
            <HistoryContext.Provider value={history}>
              <LocationContext.Provider value={location}>
                <PersistGate loading={<FullScreenProgress />} persistor={persistor}>
                  <CssBaseline />
                  <TenantMiddleware tenant={tenant}>
                    <UserMiddleware route={route} />
                  </TenantMiddleware>
                </PersistGate>
              </LocationContext.Provider>
            </HistoryContext.Provider>
          )}
        </ErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
